/* eslint-disable no-plusplus */
/* eslint-disable react/jsx-props-no-spreading */
import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import clsx from 'clsx';
import dayjs from 'dayjs';
import FileSaver from 'file-saver';
import generateUniqueId from 'generate-unique-id';
import {
  Dispatch,
  FunctionComponent,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Accept, FileError, FileRejection, useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import FileDropZone from '@/components/fileDropZone';
import Mark from '@/components/icons/Mark';
import BaseModal from '@/components/modal/baseModal';
import BasicModalFooter from '@/components/modal/basicModalFooter';
import ModalHeading from '@/components/modal/modalHeading';
import { LoadingIndicator } from '@/components/searchBar/customComponents';
import Typography from '@/components/typography';
import { getReportForSavedSelectionById } from '@/endpoints/itasca/reports/getReportForSavedSelectionById';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';
import { useModalContext } from '@/providers/ModalProvider';
import { setContent as setToastContent } from '@/redux/slices/toastSlice';

import useSortableData from '../../../hooks/useSortableData';
import MimeType from '../../../models/mime';
import { useAnalytics } from '../../../providers/AnalyticsProvider';
import {
  DBFile,
  GeneratedCSVFile,
  ReportFile,
} from '../../../redux/slices/fileUploadModalSlice';
import removeElementByIndex from '../../../utils/removeElementById';
import Button from '../button';
import Spinner from '../loading/spinner';
import Modal from '../modal';
import { ITableColumn, ITableOptions, ITableRow } from '../table';
import useDefaultSort from '../table/hooks/useDefaultTableSort';
import TableCell from '../table/tableCell';
import TableContainer from '../table/tableContainer';
import TableHeader from '../table/tableHeader';
import RowProgress from './rowProgress';

export enum FileUploadType {
  'VIEW_UPLOAD',
  'VIEW',
}

export interface IBaseFile extends ITableRow {
  container: ReactNode;
  id: string | number;
  readonly name: string;
  readonly type: string;
  uploadDate: string;
  onClick?: () => void | null;
}

export type CustomValidation =
  | (<T extends File>(file: T) => FileError | FileError[] | null)
  | undefined;

type OnUpload = (file: File) => Promise<{ id: string | number } | null | void>;

interface IProps {
  isOpen?: boolean;
  isFetchingFiles?: boolean;
  title?: string;
  setAcceptedFiles?: Dispatch<SetStateAction<IBaseFile[]>>;
  handleOnClose: () => void;
  type?: FileUploadType;
  files?: (DBFile | ReportFile | GeneratedCSVFile)[];
  onUpload?: OnUpload;
  onDelete?: (fileId: string | number) => void;
  onDeleteGeneratedCSV?: (fileId: string | number) => void;
  onDeleteGeneratedPDF?: (reportId: string | number) => void;
  acceptedFileType?: Accept;
  acceptedFileString?: string;
  customValidation?: CustomValidation;
  savedSelectionId?: number | undefined;
}

const columns: ITableColumn<keyof IBaseFile>[] = [
  {
    alignment: 'left',
    headerName: '',
    key: 'type',
  },
  {
    alignment: 'left',
    headerName: 'File Name',
    key: 'name',
  },
  {
    alignment: 'left',
    headerName: 'Upload Date',
    key: 'uploadDate',
  },
  {
    alignment: 'left',
    headerName: '',
    key: 'container',
  },
];

const tableOptions: ITableOptions<IBaseFile> = {
  colWidths: [50 / 400, 120 / 400, 120 / 400, 110 / 400],
  dataType: {
    container: 'react-node',
    type: 'file-type',
  },
  defaultKey: 'uploadDate',
  defaultSortDirection: 'descending',
};

/**
 *
 * @param file
 * @returns the MimeType of the file based on the extension of the file name
 */
function getFileType(file: DBFile | GeneratedCSVFile) {
  if (file.name) {
    const extensions =
      file.name.match(/\.[0-9a-z]+$/i) || file.file_name.match(/\.[0-9a-z]+$/i);
    // Will get the last extension to prevent more than one extension types in file name
    const lastExtension = extensions?.at(-1);

    switch (lastExtension) {
      case '.csv':
        return MimeType.csv;
      case '.doc':
        return MimeType.doc;
      case '.docx':
        return MimeType.docx;
      case '.jpg':
      case '.jpeg':
        return MimeType.jpeg;
      case '.pdf':
        return MimeType.pdf;
      case '.png':
        return MimeType.png;
      case '.xls':
      case '.xlsx':
        return MimeType.xlsx;
      default:
        return '';
    }
  }

  return '';
}

/**
 * Converts base64 img from backend to a url that users can view the report at
 * @param base64Url string
 * @returns string
 */
const convertBase64ImgToFileUrl = (base64Url) => {
  const bstr = atob(base64Url);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  // eslint-disable-next-line no-loops/no-loops
  for (n > 0; n--; ) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  const blob = new Blob([u8arr], { type: 'application/pdf' });
  const file = new File([blob], 'generated-report.pdf', {
    type: 'application/pdf',
  });

  return { newFile: file, url: URL.createObjectURL(file) };
};

/**
 * Converts a file into a row for the table
 * @param file
 * @param setDeleteId
 * @param type
 * @param onUpload
 * @returns
 */
function convertToFileUploadRow(
  file: File | DBFile | ReportFile | GeneratedCSVFile,
  setDeleteId: Dispatch<SetStateAction<string | number | null>>,
  setGeneratedCSVDeleteId: Dispatch<SetStateAction<string | number | null>>,
  setPDFReportDeleteId: Dispatch<SetStateAction<string | number | null>>,
  type?: FileUploadType,
  onAdd?: (fileId: string | number, file: File) => void,
  onClick?: (
    fileId: string | number,
    fileName: string,
    shouldOpenNewTab?: boolean
  ) => Promise<void>,
  trackDownload?: (file: File, url?: string) => void
): IBaseFile {
  const isFileBlob = !('url' in file);
  const isReport = 'isReport' in file;
  const isGeneratedCSV = 'csv_data' in file;
  const hasCreatedDate = 'created_at' in file;

  let blob: Blob | null = null;
  let url: string = '';
  const fileId: string | number =
    isFileBlob && !isGeneratedCSV && !isReport ? generateUniqueId() : file.id;

  const fileType =
    isFileBlob && !isGeneratedCSV ? file.type : getFileType(file);
  const uploadDate = hasCreatedDate
    ? dayjs(file.created_at).format('MM-DD-YYYY')
    : dayjs().format('MM-DD-YYYY');

  if (isGeneratedCSV) {
    blob = new Blob([file.csv_data], { type: fileType });
  } else if (isFileBlob && !isReport) {
    blob = new Blob([file], { type: file.type });
    url = URL.createObjectURL(blob);
  } else if (isReport) {
    // do nothing
  } else {
    url = file.url;
  }

  if (isFileBlob && !isReport && !isGeneratedCSV) {
    onAdd?.(fileId, file);
  }

  return {
    container: (
      <div className="flex h-full items-center justify-center gap-x-2 px-4">
        <Button
          className="flex items-center"
          title="Download File"
          type="submit"
          variant="icon"
          onClick={(e) => {
            e.stopPropagation();

            if (isGeneratedCSV) {
              FileSaver.saveAs(blob, file.name);
              trackDownload?.({ name: file.name } as File);
            } else if (isFileBlob && !isReport) {
              FileSaver.saveAs(blob, file.name);
              trackDownload?.({ name: file.name } as File);
            } else if (isReport) {
              onClick?.(fileId, file.name, false);
              trackDownload?.({ name: file.name } as File);
            } else {
              FileSaver.saveAs(file.url, file.file_name);
              trackDownload?.({ name: file.name } as File, file.url);
            }
          }}
        >
          <span className="material-icons-outlined text-md text-gray-70">
            download
          </span>
        </Button>

        {type === FileUploadType.VIEW_UPLOAD && (
          <Button
            className="flex items-center"
            title="Delete File"
            type="button"
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();

              if (isGeneratedCSV) setGeneratedCSVDeleteId(fileId);
              if (isReport) setPDFReportDeleteId(fileId);
              else setDeleteId(fileId);
            }}
          >
            <span className="material-icons text-gray-70">delete</span>
          </Button>
        )}
      </div>
    ),
    id: fileId,
    name: file.name ? file.name : 'Generated-Pdf.pdf',
    onClick: () => {
      if (isGeneratedCSV) {
        FileSaver.saveAs(blob, file.name);
      } else if (fileType.includes('image')) {
        window.open(url, '_blank');
      } else if (isFileBlob && !isReport) {
        FileSaver.saveAs(blob, file.name);
      } else if (isReport && onClick) {
        onClick?.(file.id, file.name);
      } else if (!isReport) {
        FileSaver.saveAs(url, file.file_name);
      }
    },
    type: fileType,
    uploadDate,
  };
}

enum Status {
  UPLOADING,
  FINISH,
  FAIL,
}

interface IIdMap {
  [key: string | number]: {
    dbId: string | number | null;
    status: Status;
  };
}

const FileUploadModal: FunctionComponent<IProps> = ({
  handleOnClose,
  acceptedFileString = '',
  acceptedFileType = undefined,
  customValidation = undefined,
  files = undefined,
  isFetchingFiles = false,
  isOpen = false,
  onDelete = undefined,
  onDeleteGeneratedCSV = undefined,
  onDeleteGeneratedPDF = undefined,
  onUpload = undefined,
  savedSelectionId = undefined,
  setAcceptedFiles = undefined,
  title = '',
  type = FileUploadType.VIEW_UPLOAD,
}) => {
  const { setModalContent, clearModalContent } = useModalContext();
  const [fileUploadRows, setFileUploadRows] = useState<IBaseFile[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const { track } = useAnalytics();
  const [generatedCSVDeleteId, setGeneratedCSVDeleteId] = useState<
    string | number | null
  >(null);
  const [pdfReportDeleteId, setPDFReportDeleteId] = useState<
    string | number | null
  >(null);
  const dispatch = useDispatch();
  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);
  const [idMap, setIdMap] = useState<IIdMap>({});

  function fileValidator(file: File) {
    if (!file.type) {
      const splitName = file.name.split('.');
      const fileType = splitName[splitName.length - 1];
      const blob = new Blob([file], { type: MimeType[fileType] });
      const newFile = new File([blob], file.name, {
        type: MimeType[fileType],
      });
      if (newFile.type) {
        if (customValidation) {
          return customValidation(newFile);
        }
        return null;
      }

      return {
        code: 'unknown file type',
        message: 'This file has an unknown file type',
      };
    }

    if (customValidation) {
      return customValidation(file);
    }
    return null;
  }

  function trackDownload(file: File, url?: string) {
    track({
      data: {
        file_name: file.name,
        file_type: file.type || file.name.split('.').pop(),
        file_url: url,
      },
      event: TrackingEvent.FILE_DOWNLOADED,
    });
  }

  /** Triggers a TagManager event when a file based on the parameters */
  const fileUploadedDeletedAnalytics = useCallback(
    (upload: boolean, file: File) => {
      if (!upload) {
        track({
          data: {
            file_name: file.name,
            file_type: file.type,
          },
          event: TrackingEvent.FILE_DELETED,
        });
      } else {
        track({
          data: { file_name: file.name, file_type: file.type },
          event: TrackingEvent.FILE_UPLOADED,
        });
      }
    },
    [track]
  );

  const onClick = useCallback(
    async (
      fileId: string | number,
      fileName: string,
      openInNewTab: boolean = true
    ) => {
      try {
        if (!savedSelectionId) throw new Error('No saved selection id');

        setIdMap((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            dbId: null,
            status: Status.UPLOADING,
          },
        }));

        const data = await getReportForSavedSelectionById({
          reportId: fileId,
          savedSelectionId,
        });
        setIdMap((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            dbId: fileId,
            status: Status.FINISH,
          },
        }));
        const { url } = convertBase64ImgToFileUrl(data.res?.data.pdf_data);

        if (openInNewTab) {
          window.open(url, '_blank');
        } else {
          FileSaver.saveAs(url, fileName);
        }
      } catch (err) {
        dispatch(
          setToastContent({
            action: undefined,
            show: true,
            text: 'Failed to fetch report, please try again.',
            type: 'dismiss',
          })
        );
      }
    },
    [dispatch, savedSelectionId]
  );

  /** Used to keep track of files that are dropped/uploaded */
  const onAdd = useCallback(
    (fileId: string | number, file: File) => {
      setIdMap((prev) => ({
        ...prev,
        [fileId]: {
          dbId: null,
          status: Status.UPLOADING,
        },
      }));

      onUpload?.(file).then((val) => {
        if (val) {
          setIdMap((prev) => ({
            ...prev,
            [fileId]: {
              dbId: val.id,
              status: Status.FINISH,
            },
          }));
          fileUploadedDeletedAnalytics(true, file);
        }
      });
    },
    [fileUploadedDeletedAnalytics, onUpload]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejects: FileRejection[]) => {
      const rowPromises = acceptedFiles.map((file) =>
        convertToFileUploadRow(
          file,
          setDeleteId,
          setGeneratedCSVDeleteId,
          setPDFReportDeleteId,
          type,
          onAdd,
          onClick,
          trackDownload
        )
      );
      const rows = await Promise.all(rowPromises);

      setFileUploadRows((prevState) => [...prevState, ...rows]);
      setFileRejections(rejects);
    },
    [onAdd, onClick, type]
  );

  // Hooks
  const {
    items: sortedRows,
    requestSort,
    sortConfig,
  } = useSortableData(fileUploadRows);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptedFileType,
    onDrop,
    validator: fileValidator,
  });
  useDefaultSort(requestSort, tableOptions);

  /** Clear file rejections on close */
  useEffect(() => {
    if (!isOpen) {
      setFileRejections([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!files?.length) return;

    const f = files.map((file) =>
      convertToFileUploadRow(
        file,
        setDeleteId,
        setGeneratedCSVDeleteId,
        setPDFReportDeleteId,
        type,
        undefined,
        onClick,
        trackDownload
      )
    );

    Promise.all(f).then((val) => {
      setFileUploadRows(val);
    });
  }, [files, onClick, type]);

  useEffect(() => {
    setAcceptedFiles?.(fileUploadRows);
  }, [fileUploadRows, setAcceptedFiles]);

  const handleDeleteIdChanged = useCallback(
    (
      index: number,
      onActionCallback: ((fileId: string | number) => void) | undefined
    ) => {
      if (index > -1) {
        setModalContent(
          <BaseModal
            Footer={
              <BasicModalFooter
                primaryButtonText="Delete"
                secondaryButtonText="Cancel"
                onPrimaryClick={(e) => {
                  e.stopPropagation();

                  const newFileUploadRows = removeElementByIndex(
                    fileUploadRows,
                    index
                  );
                  const file = fileUploadRows[index];
                  const fileId = idMap[file.id]?.dbId ?? file.id;

                  onActionCallback?.(fileId);

                  setFileUploadRows(newFileUploadRows);
                  fileUploadedDeletedAnalytics(false, {
                    name: file.name,
                    type: file.type,
                  } as File);
                  clearModalContent();
                }}
                onSecondaryClick={clearModalContent}
              />
            }
            Header={<ModalHeading title="Are you sure?" />}
            SupportText="Are you sure you want to delete this file?"
          />
        );
      }
    },
    [
      clearModalContent,
      fileUploadRows,
      fileUploadedDeletedAnalytics,
      idMap,
      setModalContent,
    ]
  );

  // Prevents infinite loop
  // Used to delete row by id
  useEffect(() => {
    if (deleteId !== null) {
      const index = fileUploadRows.findIndex((item) => item.id === deleteId);
      handleDeleteIdChanged(index, onDelete);
      setDeleteId(null);
    }
  }, [
    fileUploadRows,
    deleteId,
    onDelete,
    dispatch,
    idMap,
    handleDeleteIdChanged,
  ]);

  useEffect(() => {
    if (generatedCSVDeleteId !== null) {
      const index = fileUploadRows.findIndex(
        (item) => item.id === generatedCSVDeleteId
      );
      handleDeleteIdChanged(index, onDeleteGeneratedCSV);
      setGeneratedCSVDeleteId(null);
    }
  }, [
    fileUploadRows,
    generatedCSVDeleteId,
    onDeleteGeneratedCSV,
    dispatch,
    idMap,
    handleDeleteIdChanged,
  ]);

  useEffect(() => {
    if (pdfReportDeleteId !== null) {
      const index = fileUploadRows.findIndex(
        (item) => item.id === pdfReportDeleteId
      );
      handleDeleteIdChanged(index, onDeleteGeneratedPDF);
      setPDFReportDeleteId(null);
    }
  }, [
    fileUploadRows,
    handleDeleteIdChanged,
    onDeleteGeneratedPDF,
    pdfReportDeleteId,
  ]);

  // Title and close button
  const renderTitleBar = () => (
    <div className="flex items-center justify-between" id="title-bar">
      <Typography className="text-gray-100" variant="h4">
        {title}
      </Typography>
      <IconButton
        size={ButtonSize.SMALL}
        variant={ButtonVariant.GHOST}
        onClick={handleOnClose}
      >
        <Mark />
      </IconButton>
    </div>
  );

  const renderDropZoneDescription = () => (
    <Typography className="text-gray-70">
      {`The following file formats are supported: ${acceptedFileString.toUpperCase()}`}
    </Typography>
  );

  const getErrorMessage = useCallback(
    (error: FileError) => {
      let types: string[] = [];

      Object.values(acceptedFileType || {}).forEach((value) => {
        types = types.concat(value);
      });

      switch (error.code) {
        case 'file-invalid-type': {
          return `An unsupported file was uploaded. Only files with the following extensions ${types.join(
            ', '
          )} are supported.`;
        }
        default: {
          return null;
        }
      }
    },
    [acceptedFileType]
  );

  const fileRejectionItems = useMemo(() => {
    return fileRejections.map((fileRejection) => (
      <div key={fileRejection.file.name}>
        <Typography className="text-red-utility" variant="caption">
          The specified file{' '}
          <span className=" font-semibold">{fileRejection.file.name}</span>{' '}
          could not be uploaded.
        </Typography>

        {/* List of errors for the associated file */}
        <ul>
          {fileRejection.errors.map((error) => (
            <li key={error.message}>
              <Typography className="text-red-utility" variant="caption">
                &nbsp; &nbsp;
                <span>•</span>
                &nbsp;
                {getErrorMessage(error)}
              </Typography>
            </li>
          ))}
        </ul>
      </div>
    ));
  }, [fileRejections, getErrorMessage]);

  const renderDropZone = () => (
    <FileDropZone
      getInputProps={getInputProps}
      getRootProps={getRootProps}
      isDragActive={isDragActive}
    />
  );

  const renderDropZoneContainer = () => {
    if (type === FileUploadType.VIEW_UPLOAD) {
      return (
        <div
          className={clsx(
            'flex  flex-1 flex-col gap-y-3',
            !isMobile && 'min-w-[400px]'
          )}
        >
          {renderDropZoneDescription()}
          {renderDropZone()}
        </div>
      );
    }

    return null;
  };

  const renderTable = () => {
    if (!files || !files.length) return null;

    return (
      <div className="min-w-[400px] flex-1" id="file-upload-table">
        <TableContainer
          columnLength={columns.length}
          // Add this className to make table scrollable
          containerClassName="h-[256px] overflow-auto"
          options={tableOptions}
        >
          <TableHeader
            // Add this className for sticky header
            className="sticky top-0 z-70 shadow"
            columns={columns}
            options={tableOptions}
            requestSort={requestSort}
            sortConfig={sortConfig}
          />
          <tbody className="">
            {sortedRows.map((row) => (
              <tr
                key={row.id}
                className="relative h-[52px] hover:cursor-pointer hover:bg-gray-20"
                onClick={row.onClick}
              >
                {columns.map((col, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={`${col.key}-${i}`} className="h-[52px] px-2">
                    <TableCell
                      cellType={tableOptions?.dataType?.[col.key]}
                      rowItem={row[col.key]}
                    />
                  </td>
                ))}
                {idMap[row.id] && (
                  <RowProgress
                    isDoneUploading={idMap[row.id].status === Status.FINISH}
                  />
                )}
              </tr>
            ))}
            {isFetchingFiles && (
              <tr key="spinner-row" className="relative h-[52px]">
                <td key="spinner-col" colSpan={columns.length}>
                  <Spinner />
                </td>
              </tr>
            )}
          </tbody>
        </TableContainer>
        <div />
      </div>
    );
  };

  return (
    <Modal
      className={clsx(
        '!bg-transparent',
        '!left-0 right-0 !translate-x-0 items-center'
      )}
      content={
        <div
          className={clsx(
            'flex flex-col gap-y-3 bg-white p-6 shadow',
            isMobile
              ? 'min-w-[100vw] max-w-[100vw]'
              : 'min-w-[450px] max-w-[880px]'
          )}
        >
          {renderTitleBar()}

          <div className="flex flex-wrap gap-6" id="content">
            {renderDropZoneContainer()}
            {isFetchingFiles ? (
              <div className="flex min-w-[400px] flex-1 items-center justify-center">
                <LoadingIndicator />
                <Typography className="text-gray-70">Loading files</Typography>
              </div>
            ) : (
              renderTable()
            )}
          </div>
          {fileRejectionItems}
        </div>
      }
      handleOnClose={handleOnClose}
      isOpen={isOpen}
    />
  );
};

export default memo(FileUploadModal);
